<template>
  <div
    aria-label="Formulaire de modification du personnel"
    role="main"
  >
    <b-card
      :title="null"
      aria-labelledby="page-title"
    >
      <h2
        id="page-title"
        class="sr-only"
      >
        Modification d'un membre du personnel
      </h2>
      <p class="sr-only">
        Tous les champs marqués d'un astérisque (*) sont obligatoires. Utilisez des informations professionnelles et logistiques précises.
      </p>
      <ValidationObserver
        ref="staffFormObserver"
      >
        <b-form
          ref="staffForm"
          aria-describedby="form-desc"
          @submit.prevent="updateStaff"
        >
        <!-- Sélection du rôle (déplacé en haut) -->
        <h5
          id="section-role"
          class="mb-2"
          aria-label="Sélection du rôle"
        >
          <feather-icon
            icon="ShieldIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Sélection du rôle
        </h5>

        <b-form-group label-class="form-label-premium"
          label="Rôle *"
          label-for="role"
          description="Définissez le rôle logistique de ce membre (ex : Commercial, Magasinier, Support Client, Comptable)."
          :invalid-feedback="submitted && !form.role ? 'Le rôle est requis' : ''"
          :state="roleState"
        >
          <v-select
            id="role"
            v-model="form.role"
            :options="staffRoles"
            label="label"
            :reduce="role => role.value"
            placeholder="Sélectionnez un rôle"
            :class="[
              'enhanced-select',
              submitted && !form.role ? 'is-invalid' : '',
              form.role ? 'is-valid' : ''
            ]"
            :clearable="true"
            :searchable="true"
            aria-label="Sélection du rôle logistique"
            @input="onRoleChange"
          />
        </b-form-group>
        <div
          id="form-desc"
          class="sr-only"
        >
          Ce formulaire permet de modifier les informations d'un membre du personnel logistique. Veuillez vérifier chaque section avant de valider.
        </div>

        <b-form-group label-class="form-label-premium"
          label="Éligible aux commissions"
          label-for="can_receive_commission"
          class="mb-2"
        >
          <b-form-checkbox
            id="can_receive_commission"
            v-model="form.can_receive_commission"
            @change="onCommissionEligibilityChange"
          >
            Autoriser les commissions par commande
          </b-form-checkbox>
        </b-form-group>

        <div v-if="form.can_receive_commission">
          <h5
            id="section-commission"
            class="mb-2"
          >
            <feather-icon
              icon="PercentIcon"
              class="mr-1"
              aria-hidden="true"
            />
            Paramètres de commission
          </h5>
          <b-row>
            <b-col md="4">
              <b-form-group label-class="form-label-premium"
                label="Commission par commande (DT)"
                label-for="commission_rate"
              >
                <b-form-input class="premium-input"
                  id="commission_rate"
                  v-model="form.commission_rate"
                  :formatter="formatPrice"
                  type="text"
                  inputmode="decimal"
                  placeholder="0.200"
                  :state="submitted && form.commission_rate ? true : null"
                  aria-required="true"
                  @keypress="allowOnlyDecimalNumbers"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label-class="form-label-premium"
                label="Date de début"
                label-for="commission_start_date"
              >
                <b-form-input class="premium-input"
                  id="commission_start_date"
                  v-model="form.commission_start_date"
                  type="date"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label-class="form-label-premium"
                label="Date de fin"
                label-for="commission_end_date"
              >
                <b-form-input class="premium-input"
                  id="commission_end_date"
                  v-model="form.commission_end_date"
                  type="date"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-form-text>
            Laissez ces champs vides si vous ne souhaitez pas créer une nouvelle période de commission.
          </b-form-text>
          <div class="mt-3">
            <h6 class="mb-2">
              Périodes existantes
            </h6>
            <div
              v-if="loadingCommissions"
              class="text-center py-3"
            >
              <b-spinner
                variant="primary"
                small
              />
            </div>
            <div v-else class="premium-table-wrapper">
            <b-table
              :items="commissionRates"
              :fields="commissionFields"
              responsive
              small
              hover
            >
              <template #cell(rate)="data">
                <span class="font-weight-bold text-success">
                  {{ formatAmount(data.item.rate) }} DT
                </span>
              </template>
              <template #cell(period)="data">
                {{ formatShortDate(data.item.start_date) }} - {{ formatShortDate(data.item.end_date) }}
              </template>
              <template #cell(status)="data">
                <b-badge :variant="getCommissionStatusVariant(data.item)">
                  {{ getCommissionStatusLabel(data.item) }}
                </b-badge>
              </template>
              <template #cell(actions)="data">
                <b-button
                  v-if="canDeleteCommission(data.item)"
                  variant="outline-danger"
                  size="sm"
                  @click="confirmDeleteCommission(data.item)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="14"
                  />
                </b-button>
              </template>
            </b-table>
            </div>
            <div
              v-if="!loadingCommissions && commissionRates.length === 0"
              class="text-center text-muted py-2"
            >
              Aucune période de commission définie.
            </div>
          </div>
        </div>
        <!-- Informations personnelles -->
        <h5
          id="section-infos"
          class="mb-2"
          aria-label="Informations personnelles"
        >
          <feather-icon
            icon="UserIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Informations personnelles
        </h5>

        <b-row>
          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Nom complet *"
              label-for="username"
              description="Nom et prénom du membre du personnel, tel qu'indiqué sur les documents officiels."
            >
              <b-form-input class="premium-input"
                id="username"
                v-model="form.username"
                :state="nameState"
                placeholder="Entrez le nom complet"
                aria-required="true"
                aria-label="Nom complet"
              />
              <b-form-invalid-feedback v-if="submitted && !form.username">
                Le nom complet est requis pour l'identification logistique.
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Adresse email professionnelle *"
              label-for="email"
              description="Utilisez l'adresse email professionnelle pour la communication interne."
            >
              <b-input-group class="input-group-merge">
                <b-form-input class="premium-input"
                  id="email"
                  v-model="form.email"
                  type="email"
                  :state="emailState"
                  placeholder="Entrez l'adresse email professionnelle"
                  aria-required="true"
                  aria-label="Adresse email professionnelle"
                />
                <b-input-group-append is-text>
                  <feather-icon
                    icon="RefreshCwIcon"
                    :class="form.username && form.username.trim().length > 0 ? 'cursor-pointer text-primary' : 'text-muted cursor-not-allowed'"
                    v-b-tooltip.hover="emailGenerationTooltip"
                    @click="form.username && form.username.trim().length > 0 ? generateCompanyEmail() : null"
                  />
                </b-input-group-append>
              </b-input-group>
              <b-form-invalid-feedback v-if="submitted && !form.email">
                L'adresse email professionnelle est requise.
              </b-form-invalid-feedback>
              <b-form-invalid-feedback v-if="submitted && form.email && !emailState">
                Veuillez entrer une adresse email valide.
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Téléphone principal *"
              label-for="phone"
              description="Numéro de téléphone professionnel (8 chiffres, sans espaces). Champ obligatoire."
            >
              <b-form-input class="premium-input"
                id="phone"
                v-model="form.phone"
                placeholder="Entrez le numéro de téléphone"
                type="tel"
                maxlength="8"
                :formatter="formatPhone"
                :state="phoneState"
                aria-label="Téléphone principal"
                @keypress="allowOnlyNumbers"
              />
              <b-form-invalid-feedback v-if="form.phone && !phoneState">
                Le numéro de téléphone doit contenir 8 chiffres.
              </b-form-invalid-feedback>
              <b-form-text>Format : 8 chiffres, sans espaces.</b-form-text>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label-class="form-label-premium"
              label="Téléphone secondaire"
              label-for="phone2"
              description="Numéro de téléphone secondaire (optionnel)."
            >
              <b-form-input class="premium-input"
                id="phone2"
                v-model="form.phone2"
                placeholder="Entrez le numéro de téléphone secondaire (optionnel)"
                type="tel"
                maxlength="8"
                :formatter="formatPhone"
                aria-label="Téléphone secondaire"
                @keypress="allowOnlyNumbers"
              />
              <b-form-text>Format : 8 chiffres, sans espaces.</b-form-text>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label-class="form-label-premium"
          label="Adresse professionnelle"
          label-for="address"
          description="Adresse complète du membre du personnel (rue, ville, code postal)."
        >
          <b-form-textarea
            id="address"
            v-model="form.address"
            placeholder="Entrez l'adresse complète"
            rows="3"
            aria-label="Adresse professionnelle"
          />
        </b-form-group>

        <b-form-group label-class="form-label-premium"
          label="CIN (Carte d'Identité Nationale)"
          label-for="cin"
          description="Numéro de CIN à 8 chiffres (optionnel)."
        >
          <b-form-input class="premium-input"
            id="cin"
            v-model="form.cin"
            placeholder="Entrez le numéro de CIN (optionnel)"
            maxlength="8"
            :formatter="formatCIN"
            :state="cinState"
            aria-label="Numéro de CIN"
            @keypress="allowOnlyNumbers"
          />
          <b-form-invalid-feedback v-if="form.cin && !cinState">
            Le numéro de CIN doit contenir 8 chiffres.
          </b-form-invalid-feedback>
          <b-form-text>Format : 8 chiffres, sans espaces.</b-form-text>
        </b-form-group>

        <!-- Affectation au dépôt -->
        <h5
          id="section-repo"
          class="mb-2 mt-4"
          aria-label="Affectation au dépôt"
        >
          <feather-icon
            icon="HomeIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Affectation au dépôt
        </h5>

        <b-form-group label-class="form-label-premium"
          label="Dépôt *"
          label-for="repository"
          description="Sélectionnez le dépôt logistique auquel ce membre est affecté. Obligatoire pour les opérateurs de dépôt."
          :invalid-feedback="submitted && !form.repository ? 'Le dépôt est requis pour les opérateurs de dépôt' : ''"
          :state="repositoryState"
        >
          <v-select
            id="repository"
            v-model="form.repository"
            :options="repositories"
            label="name"
            :reduce="repo => repo.id"
            placeholder="Sélectionnez un dépôt"
            :class="[
              'enhanced-select',
              submitted && !form.repository ? 'is-invalid' : '',
              form.repository ? 'is-valid' : ''
            ]"
            :clearable="true"
            :searchable="true"
            :loading="repositoriesLoading"
            :disabled="repositoriesLoading"
            aria-label="Sélection du dépôt logistique"
            @input="onRepositoryChange"
          >
            <template #no-options>
              <span v-if="repositoriesLoading">Chargement des dépôts...</span>
              <span v-else>Aucun dépôt disponible</span>
            </template>
            <template #selected-option="{ name }">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="HomeIcon"
                  size="16"
                  class="mr-2"
                  style="color: #114092;"
                  aria-hidden="true"
                />
                <span>{{ name }}</span>
              </div>
            </template>
            <template #option="{ name }">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="HomeIcon"
                  size="16"
                  class="mr-2"
                  style="color: #114092;"
                  aria-hidden="true"
                />
                <span>{{ name }}</span>
              </div>
            </template>
            <template #spinner="{ loading }">
              <div
                v-if="loading"
                class="vs__spinner"
              >
                <div class="loading-dots">
                  <div class="dot1" />
                  <div class="dot2" />
                  <div class="dot3" />
                </div>
              </div>
            </template>
          </v-select>
        </b-form-group>

        <!-- Boutons d'action -->
        <div
          class="d-flex justify-content-between mt-4"
          aria-label="Actions du formulaire"
        >
          <b-button
            variant="outline-secondary"
            :to="{ name: 'staff-list' }"
            aria-label="Annuler et revenir à la liste du personnel"
          >
            <feather-icon
              icon="XIcon"
              size="16"
              class="mr-1"
              aria-hidden="true"
            />
            Annuler
          </b-button>
          <span
            id="staff-edit-save-btn-wrapper"
            class="d-inline-block"
          >
            <b-button
              variant="primary"
              type="submit"
              :disabled="!isFormValid || isSubmitting"
              :class="{ 'not-allowed': !isFormValid || isSubmitting }"
              aria-label="Enregistrer les modifications"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-1"
                aria-hidden="true"
              />
              <feather-icon
                v-else
                icon="SaveIcon"
                size="16"
                class="mr-1"
                aria-hidden="true"
              />
              Enregistrer
            </b-button>
            <b-tooltip
              v-if="!isFormValid || isSubmitting"
              target="staff-edit-save-btn-wrapper"
              triggers="hover focus"
              placement="top"
            >
              {{ saveButtonTooltipMessage }}
            </b-tooltip>
          </span>
        </div>
      </b-form>
      </ValidationObserver>
    </b-card>
    <b-modal
      v-model="showCommissionDeleteModal"
      title="Supprimer la période"
      centered
      ok-variant="danger"
      cancel-variant="outline-secondary"
      @ok="handleCommissionDelete"
    >
      <p class="mb-2">
        Confirmer la suppression de cette période de commission ?
      </p>
      <p class="text-danger mb-2">
        Cette action est irréversible.
      </p>
      <div v-if="commissionToDelete">
        <div class="font-weight-bold">
          {{ formatShortDate(commissionToDelete.start_date) }} - {{ formatShortDate(commissionToDelete.end_date) }}
        </div>
        <div class="text-muted">
          {{ formatAmount(commissionToDelete.rate) }} DT / commande
        </div>
      </div>
      <template #modal-ok>
        <feather-icon
          icon="TrashIcon"
          size="14"
          class="mr-1"
        />
        Supprimer
      </template>
      <template #modal-cancel>
        <feather-icon
          icon="XIcon"
          size="14"
          class="mr-1"
        />
        Annuler
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BRow,
  BCol,
  BSpinner,
  BFormInvalidFeedback,
  BFormText,
  BTable,
  BBadge,
  BModal,
  BTooltip,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'StaffEdit',

  components: {
    BCard,
    ValidationObserver,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BRow,
    BCol,
    BSpinner,
    BFormInvalidFeedback,
    BFormText,
    BTable,
    BBadge,
    BModal,
    BTooltip,
    vSelect,
  },

  data() {
    return {
      isSubmitting: false,
      submitted: false,
      repositories: [],
      repositoriesLoading: true,
      loadingCommissions: false,
      showCommissionDeleteModal: false,
      commissionToDelete: null,
      commissionRates: [],
      commissionFields: [
        { key: 'rate', label: 'Taux', class: 'text-center' },
        { key: 'period', label: 'Période' },
        { key: 'status', label: 'Statut', class: 'text-center' },
        { key: 'actions', label: '', class: 'text-center' },
      ],
      form: {
        username: '',
        email: '',
        phone: '',
        phone2: '',
        address: '',
        cin: '',
        repository: null,
        role: null,
        can_receive_commission: false,
        commission_rate_id: null,
        commission_rate: '',
        commission_start_date: '',
        commission_end_date: '',
      },
      staffRoles: [
        { label: 'Commercial', value: 'sales' },
        { label: 'Magasinier', value: 'warehouse_operator' },
        { label: 'Support Client', value: 'customer_support' },
        { label: 'Comptable', value: 'accountant' },
      ],
    }
  },

  computed: {
    companyDomain() {
      return process.env.VUE_APP_COMPANY_EMAIL_DOMAIN || null
    },

    emailGenerationTooltip() {
      if (!this.companyDomain) {
        return 'Erreur: Variable d\'environnement VUE_APP_COMPANY_EMAIL_DOMAIN non définie'
      }
      if (this.form.username && this.form.username.trim().length > 0) {
        return `Générer un email par défaut à partir du nom (@${this.companyDomain}). Modifiable si nécessaire.`
      }
      return 'Veuillez d\'abord saisir le nom du staff'
    },

    nameState() {
      return this.form.username ? true : null
    },

    emailState() {
      if (!this.form.email) return null
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(this.form.email)
    },

    phoneState() {
      if (!this.form.phone) return null
      return this.form.phone.length === 8 && /^\d+$/.test(this.form.phone)
    },

    repositoryState() {
      return this.form.repository ? true : null
    },

    cinState() {
      if (!this.form.cin) return true // Optional field
      return this.form.cin.length === 8 && /^\d+$/.test(this.form.cin)
    },

    roleState() {
      return this.form.role ? true : null
    },

    isFormValid() {
      const basicValid = (
        this.form.username !== ''
        && this.form.email !== ''
        && this.emailState
        && this.form.phone !== ''
        && this.phoneState
        && this.form.role !== null
      )

      // Additional commission validation if enabled
      if (this.form.can_receive_commission) {
        const commissionRate = parseFloat(this.form.commission_rate)
        return (
          basicValid
          && this.form.commission_rate !== ''
          && !Number.isNaN(commissionRate)
          && commissionRate > 0
          && this.form.commission_start_date !== ''
          && this.form.commission_end_date !== ''
          && this.form.commission_start_date <= this.form.commission_end_date
        )
      }

      return basicValid
    },

    saveButtonTooltipMessage() {
      if (this.isSubmitting) return 'Mise à jour en cours...'

      const missing = []

      if (!this.form.role) missing.push('Rôle')
      if (!this.form.username || !this.form.username.trim()) missing.push('Nom complet')
      if (!this.form.email || !this.form.email.trim()) missing.push('Email')
      if (!this.emailState && this.form.email) return 'Veuillez entrer une adresse e-mail valide.'
      if (!this.form.phone || !this.form.phone.trim()) missing.push('Téléphone professionnel')
      if (this.form.phone && !this.phoneState) return 'Le numéro de téléphone doit contenir 8 chiffres.'

      // Commission validation messages
      if (this.form.can_receive_commission) {
        const commissionRate = parseFloat(this.form.commission_rate)
        if (!this.form.commission_rate || Number.isNaN(commissionRate) || commissionRate <= 0) {
          missing.push('Taux de commission')
        }
        if (!this.form.commission_start_date) missing.push('Date de début de commission')
        if (!this.form.commission_end_date) missing.push('Date de fin de commission')
        if (this.form.commission_start_date && this.form.commission_end_date && this.form.commission_start_date > this.form.commission_end_date) {
          return 'La date de début doit précéder la date de fin.'
        }
      }

      if (missing.length > 0) {
        if (missing.length === 1) {
          return `Veuillez renseigner le champ : ${missing[0]}.`
        }
        if (missing.length <= 3) {
          return `Veuillez remplir les champs obligatoires : ${missing.join(', ')}.`
        }
        return `Veuillez remplir les champs obligatoires manquants (${missing.length} champs).`
      }

      return 'Veuillez corriger les erreurs dans le formulaire.'
    },
  },

  created() {
    this.fetchRepositories()
    this.fetchStaffMember()
  },

  methods: {
    stripAccents(value) {
      if (!value) return ''
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },

    generateCompanyEmail() {
      if (!this.form.username || !this.form.username.trim()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Attention',
            icon: 'AlertCircleIcon',
            text: 'Veuillez d\'abord saisir le nom du staff.',
            variant: 'warning',
          },
        })
        return
      }

      const username = this.form.username.trim()
      const cleanUsername = this.stripAccents(username)
        .toLowerCase()
        .replace(/\s+/g, '.')
        .replace(/[^a-z0-9.]/g, '')

      const generatedEmail = `${cleanUsername}@${this.companyDomain}`
      this.form.email = generatedEmail

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Email généré',
          icon: 'CheckCircleIcon',
          text: `Email: ${generatedEmail}`,
          variant: 'success',
        },
      })
    },

    allowOnlyNumbers(event) {
      const { charCode, target } = event
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }
      if (target.id === 'phone' && this.form.phone && this.form.phone.length >= 8) {
        event.preventDefault()
      }
      if (target.id === 'phone2' && this.form.phone2 && this.form.phone2.length >= 8) {
        event.preventDefault()
      }
      if (target.id === 'cin' && this.form.cin && this.form.cin.length >= 8) {
        event.preventDefault()
      }
    },

    formatPhone(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    formatCIN(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    allowOnlyDecimalNumbers(event) {
      const { charCode } = event
      const currentValue = event.target.value || ''

      // Allow digits (0-9)
      if (charCode >= 48 && charCode <= 57) {
        return true
      }

      // Allow decimal point (46 = '.')
      if (charCode === 46) {
        // Only allow one decimal point
        if (currentValue.includes('.')) {
          event.preventDefault()
          return false
        }
        return true
      }

      // Block all other characters
      event.preventDefault()
      return false
    },

    formatPrice(value) {
      // Remove all non-numeric and non-decimal characters
      let formatted = value.replace(/[^\d.]/g, '')

      // Ensure only one decimal point
      const parts = formatted.split('.')
      if (parts.length > 2) {
        formatted = `${parts[0]}.${parts.slice(1).join('')}`
      }

      // Limit to 3 decimal places
      if (parts.length === 2) {
        formatted = `${parts[0]}.${parts[1].substring(0, 3)}`
      }

      return formatted
    },

    async fetchRepositories() {
      this.repositoriesLoading = true
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: 'Impossible de charger les dépôts',
        })
      } finally {
        this.repositoriesLoading = false
      }
    },

    async fetchStaffMember() {
      const staffId = this.$route.params.id
      try {
        const { data } = await axios.get(`/api/auth/staff-members/${staffId}/`)
        this.form = {
          username: data.username,
          email: data.email,
          phone: data.phone,
          phone2: data.phone2,
          address: data.address,
          cin: data.cin,
          repository: data.repository,
          role: data.role,
          can_receive_commission: data.can_receive_commission ?? false,
          commission_rate_id: null,
          commission_rate: '',
          commission_start_date: '',
          commission_end_date: '',
        }
        if (this.form.can_receive_commission) {
          await this.loadCommissionRates()
        } else {
          this.commissionRates = []
        }
      } catch (error) {
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: 'Impossible de charger les informations du membre',
        })
      }
    },

    onRepositoryChange(value) {
      this.form.repository = value
      if (value && this.submitted) {
        this.submitted = false
        this.$nextTick(() => {
          this.submitted = true
        })
      }
    },

    validateForm() {
      this.submitted = true
      if (!this.form.username) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Le nom complet est requis',
        })
        return false
      }
      if (!this.form.email || !this.emailState) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Une adresse email valide est requise',
        })
        return false
      }
      // Only require repository for warehouse_operator
      if (this.form.role === 'warehouse_operator' && !this.form.repository) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Veuillez sélectionner un dépôt',
        })
        return false
      }
      if (!this.form.role) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Veuillez sélectionner un rôle',
        })
        return false
      }
      if (this.form.can_receive_commission) {
        const hasAnyCommissionField = this.form.commission_rate
          || this.form.commission_start_date
          || this.form.commission_end_date
        if (hasAnyCommissionField) {
          const commissionRate = parseFloat(this.form.commission_rate)
          if (!this.form.commission_rate || Number.isNaN(commissionRate) || commissionRate <= 0) {
            this.showToast({
              variant: 'danger',
              title: 'Validation échouée',
              text: 'Le taux de commission doit être supérieur à zéro',
            })
            return false
          }
          if (!this.form.commission_start_date || !this.form.commission_end_date) {
            this.showToast({
              variant: 'danger',
              title: 'Validation échouée',
              text: 'Les dates de commission sont requises',
            })
            return false
          }
          if (this.form.commission_start_date > this.form.commission_end_date) {
            this.showToast({
              variant: 'danger',
              title: 'Validation échouée',
              text: 'La date de début doit précéder la date de fin',
            })
            return false
          }
        }
      }
      if (!this.form.phone || !this.phoneState) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Le numéro de téléphone doit contenir 8 chiffres',
        })
        return false
      }
      if (this.form.cin && !this.cinState) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Le numéro de CIN doit contenir 8 chiffres',
        })
        return false
      }
      return true
    },

    async updateStaff() {
      if (this.isSubmitting) return
      if (!this.validateForm()) return
      this.isSubmitting = true
      const staffId = this.$route.params.id
      try {
        // Ensure repository is an ID, not an object
        const payload = {
          ...this.form,
          repository: typeof this.form.repository === 'object' && this.form.repository !== null
            ? this.form.repository.id
            : this.form.repository,
          role: this.form.role,
        }
        delete payload.commission_rate_id
        delete payload.commission_rate
        delete payload.commission_start_date
        delete payload.commission_end_date
        await axios.put(`/api/auth/staff-members/${staffId}/update/`, payload)
        if (this.form.can_receive_commission
          && this.form.commission_rate
          && this.form.commission_start_date
          && this.form.commission_end_date) {
          try {
            const commissionPayload = {
              salesperson: staffId,
              rate: this.form.commission_rate,
              start_date: this.form.commission_start_date,
              end_date: this.form.commission_end_date,
            }
            if (this.form.commission_rate_id) {
              await axios.put(
                `/api/commissions/commission-rates/${this.form.commission_rate_id}/`,
                commissionPayload,
              )
            } else {
              await axios.post('/api/commissions/commission-rates/', commissionPayload)
            }
            await this.loadCommissionRates()
          } catch (error) {
            const message = this.extractErrorMessage(
              error,
              this.form.commission_rate_id
                ? 'Erreur lors de la mise à jour de la commission'
                : 'Erreur lors de la création de la commission',
            )
            this.showToast({
              variant: 'danger',
              title: 'Erreur',
              text: message,
            })
            return
          }
        }
        this.showToast({
          variant: 'success',
          title: 'Succès',
          text: 'Membre du personnel mis à jour avec succès',
        })
        setTimeout(() => {
          this.$router.push({ name: 'staff-list' })
        }, 1000)
      } catch (error) {
        const errorMessage = this.extractErrorMessage(
          error,
          'Erreur lors de la mise à jour du membre du personnel',
        )
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: errorMessage,
        })
      } finally {
        this.isSubmitting = false
      }
    },

    onRoleChange() {
      if (this.form.role !== 'sales') {
        this.form.commission_rate_id = null
        this.form.commission_rate = ''
        this.form.commission_start_date = ''
        this.form.commission_end_date = ''
        this.form.can_receive_commission = false
        this.commissionRates = []
      } else if (!this.form.can_receive_commission) {
        this.form.can_receive_commission = true
      }
      if (this.form.can_receive_commission && !this.commissionRates.length) {
        if (!this.form.commission_start_date) {
          this.form.commission_start_date = this.getTodayDateString()
        }
        if (!this.form.commission_end_date) {
          this.form.commission_end_date = this.getNextYearDateString()
        }
        this.loadCommissionRates()
      }
    },

    onCommissionEligibilityChange() {
      if (this.form.can_receive_commission) {
        if (!this.form.commission_start_date && !this.commissionRates.length) {
          this.form.commission_start_date = this.getTodayDateString()
        }
        if (!this.form.commission_end_date && !this.commissionRates.length) {
          this.form.commission_end_date = this.getNextYearDateString()
        }
        this.loadCommissionRates()
      } else {
        this.form.commission_rate_id = null
        this.form.commission_rate = ''
        this.form.commission_start_date = ''
        this.form.commission_end_date = ''
        this.commissionRates = []
      }
    },

    getTodayDateString() {
      const today = new Date()
      return today.toISOString().split('T')[0]
    },

    getNextYearDateString() {
      const nextYear = new Date()
      nextYear.setFullYear(nextYear.getFullYear() + 1)
      return nextYear.toISOString().split('T')[0]
    },

    confirmDeleteCommission(rate) {
      this.commissionToDelete = rate
      this.showCommissionDeleteModal = true
    },

    async handleCommissionDelete() {
      if (!this.commissionToDelete) return
      await this.deleteCommissionRate(this.commissionToDelete)
      this.commissionToDelete = null
      this.showCommissionDeleteModal = false
    },

    formatShortDate(dateString) {
      if (!dateString) return 'Non définie'
      return new Date(dateString).toLocaleDateString('fr-FR')
    },

    formatAmount(value) {
      if (value === null || value === undefined) return '0.000'
      return parseFloat(value).toFixed(3)
    },

    getCommissionStatusVariant(rate) {
      const today = new Date().toISOString().split('T')[0]
      if (rate.end_date < today) return 'secondary'
      if (rate.start_date <= today && rate.end_date >= today) return 'success'
      return 'info'
    },

    getCommissionStatusLabel(rate) {
      const today = new Date().toISOString().split('T')[0]
      if (rate.end_date < today) return 'Terminé'
      if (rate.start_date <= today && rate.end_date >= today) return 'Actif'
      return 'À venir'
    },

    canDeleteCommission(rate) {
      const today = new Date().toISOString().split('T')[0]
      return rate.start_date > today
    },

    async deleteCommissionRate(rate) {
      try {
        await axios.delete(`/api/commissions/commission-rates/${rate.id}/`)
        this.showToast({
          variant: 'success',
          title: 'Succès',
          text: 'Période supprimée',
        })
        this.loadCommissionRates()
      } catch (error) {
        const message = error.response?.data?.error || 'Erreur lors de la suppression'
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: message,
        })
      }
    },

    async loadCommissionRates() {
      if (!this.$route.params.id) return
      this.loadingCommissions = true
      try {
        const { data } = await axios.get('/api/commissions/commission-rates/', {
          params: { salesperson: this.$route.params.id },
        })
        this.commissionRates = data.results || data
        this.populateCommissionForm()
      } catch (error) {
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: 'Erreur lors du chargement des commissions',
        })
        this.commissionRates = []
        this.populateCommissionForm()
      } finally {
        this.loadingCommissions = false
      }
    },

    populateCommissionForm() {
      const editableRate = this.getEditableCommissionRate()
      if (!editableRate) {
        this.form.commission_rate_id = null
        this.form.commission_rate = ''
        if (this.form.can_receive_commission) {
          if (!this.form.commission_start_date) {
            this.form.commission_start_date = this.getTodayDateString()
          }
          if (!this.form.commission_end_date) {
            this.form.commission_end_date = this.getNextYearDateString()
          }
        }
        return
      }

      this.form.commission_rate_id = editableRate.id
      this.form.commission_rate = editableRate.rate
      this.form.commission_start_date = editableRate.start_date
      this.form.commission_end_date = editableRate.end_date
    },

    getEditableCommissionRate() {
      if (!this.commissionRates.length) return null

      const today = this.getTodayDateString()
      const activeRate = this.commissionRates.find(
        rate => rate.start_date <= today && rate.end_date >= today,
      )
      if (activeRate) return activeRate

      const upcomingRate = this.commissionRates.find(rate => rate.start_date > today)
      if (upcomingRate) return upcomingRate

      return this.commissionRates[this.commissionRates.length - 1]
    },

    extractErrorMessage(error, fallbackMessage) {
      const errorData = error.response?.data
      if (!errorData) return fallbackMessage
      if (typeof errorData === 'string') return errorData
      if (Array.isArray(errorData.non_field_errors) && errorData.non_field_errors.length) {
        return errorData.non_field_errors[0]
      }
      if (errorData.detail) return errorData.detail
      if (errorData.error) return errorData.error
      if (errorData.message) return errorData.message

      const firstErrorKey = Object.keys(errorData)[0]
      if (!firstErrorKey) return fallbackMessage

      const firstError = errorData[firstErrorKey]
      if (Array.isArray(firstError) && firstError.length) return firstError[0]
      if (typeof firstError === 'string') return firstError

      return fallbackMessage
    },

    showToast({ variant, title, text }) {
      let icon
      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else if (variant === 'danger') {
        icon = 'AlertTriangleIcon'
      } else {
        icon = 'AlertCircleIcon'
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      }, {
        position: 'top-right',
        timeout: 5000,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.form-group label {
  font-weight: 600;
  color: #5e5873;
}

.enhanced-select .vs__dropdown-toggle {
  border-color: #d8d6de;
  &:focus {
    border-color: #114092;
  }
}

h5 .feather {
  color: #114092;
}

.btn-primary {
  background: linear-gradient(135deg, #114092 0%, #0a2a61 100%) !important;
  border: none;
}

/* ==========================================
   Real-Time Validation Styles (CTO Standard)
   ========================================== */

/* Submit Button Cursor Feedback */
.btn.not-allowed,
.btn:disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: auto !important; /* Allow tooltip hover */
}

/* Tooltip Styling */
.tooltip {
  z-index: 9999 !important;
}

.tooltip-inner {
  max-width: 300px !important;
  text-align: left !important;
  padding: 0.75rem 1rem !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

/* Premium Input Styling with Real-time Validation */
.premium-input {
  transition: all 0.3s ease;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;

  &:focus {
    border-color: #ff0000; /* Brand color */
    box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.15);
  }

  &.is-valid {
    border-color: #28c76f !important;
    background-color: #f0fdf4;
    padding-right: 3rem; /* Space for checkmark */
  }

  &.is-invalid {
    border-color: #ea5455 !important;
    background-color: #fef2f2;
  }
}

/* Success Icon Position */
.input-success-icon {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #28c76f;
  pointer-events: none;
  z-index: 5;
  animation: checkFadeIn 0.3s ease;
}

@keyframes checkFadeIn {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

/* Animated Error Messages */
.validation-message-animate {
  display: inline-flex;
  align-items: center;
  animation: fadeInDown 0.3s ease;
  margin-top: 0.25rem;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
